import React from "react";
import { ApplicatorDto } from "../../requests/useGetApplicators";
import useUpsertApplicator, { ApplicatorUpsertDto } from "../../requests/useUpsertApplicator";
import { RowFormText } from "../Common/Forms/FormText";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import { FormSiteAssignment } from "../Common/SiteAssignment/FormSiteAssignment";
import { FormEmailList, fromEmailFields, toEmailFields } from "../Common/Forms/FormEmailList";
import useGetApplicatorSiteAssignment from "../../requests/useGetApplicatorSiteAssignment";
import useEntityForm from "../Common/useEntityForm";

type ApplicatorForm = Omit<ApplicatorUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

interface CreateEditApplicatorProps {
  initialApplicator: ApplicatorDto | null;
  onDismiss: () => void;
  onUpsert: (applicatorId: string, applicatorName: string) => void;
  mode: ModalMode;
}

export const CreateEditApplicator = (props: CreateEditApplicatorProps) => {
  const { id, sites: initialSites, emailAddresses: initialEmails, ...initialApplicator } = { ...props.initialApplicator };

  const { hookProps, control, trigger, submit, isSaving, loading, loadError, displayMessage, sites } =
    useEntityForm<ApplicatorForm, ApplicatorUpsertDto>({
      initialId: props.initialApplicator?.id,
      defaultValues: {
        ...initialApplicator,
        siteIds: [],
        emailAddresses: toEmailFields(initialEmails),
      },
      useSiteAssignment: options => useGetApplicatorSiteAssignment(props.initialApplicator?.id, options),
      useUpsert: options => useUpsertApplicator(props.initialApplicator?.id, options),
      toDto: form => ({
        ...form,
        emailAddresses: fromEmailFields(form.emailAddresses),
      }),
      onSuccess: (applicatorId, form) => {
        props.onDismiss();
        props.onUpsert(applicatorId, form.name);
      },
    });

  return (<EntityModal
    mode={props.mode}
    entityName="applicator"
    displayMessage={displayMessage}
    loading={loading}
    loadError={loadError}
    renderSiteAssignment={<FormSiteAssignment {...hookProps('siteIds')} control={control} sites={sites} />}
    onHide={props.onDismiss}
    isSaving={isSaving}
    onSubmit={submit}
    renderForm={(<>
      <RowFormText {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps('license')}
        displayName="License"
        options={{
          validate: { length: v => v.length < 51 || 'License cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps("epaNumber")}
        displayName="EPA Number"
        options={{
          validate: {
            length: v => v.length < 51 || 'EPA Number cannot be longer than 50 characters'
          }
        }} />
      <FormEmailList {...hookProps('emailAddresses')}
        control={control}
        trigger={trigger}
        disabled={props.mode === ModalMode.View}
        max={10} />
    </>)} />);
};
